// ABOUTME: Catalog dialog to quickly add products to the active cart without opening an offer.
// ABOUTME: Reuses the model-selection catalog view; clicking a product adds it and keeps the dialog open.

import type { IProduct } from '@coolshop/cool-model-selection';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@ds/coolshop';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useModelSelectionData } from '../../hooks/useModelSelectionData';
import { CatalogView } from '../OfferLineItems/CatalogView';
import { useAddToCart } from './useAddToCart';

interface CartCatalogDialogProps {
    open: boolean;
    onClose: () => void;
}

export function CartCatalogDialog({ open, onClose }: CartCatalogDialogProps) {
    const { formatMessage } = useIntl();
    const { addToCart } = useAddToCart();

    const {
        data: modelSelectionData,
        loading: catalogLoading,
        error: catalogError,
        fetchData,
        loadSubranges,
        loadSearchResults,
        loadFilters,
        fetchWithFilters,
    } = useModelSelectionData();

    useEffect(() => {
        if (open && !modelSelectionData) {
            fetchData();
        }
    }, [open, modelSelectionData, fetchData]);

    const handleProductClick = useCallback(
        (product: IProduct) => {
            addToCart(decodeURIComponent(atob(product.id)), 1);
        },
        [addToCart],
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                <Typography variant="h4">
                    {formatMessage({
                        id: 'cpq.cart.catalog.title',
                        defaultMessage: 'Add products to cart',
                    })}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box height="80vh">
                    {catalogLoading && (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                            <CircularProgress size="medium" />
                        </Stack>
                    )}
                    {!catalogLoading && catalogError && (
                        <Alert severity="error">{catalogError}</Alert>
                    )}
                    {!catalogLoading && !catalogError && modelSelectionData && (
                        <CatalogView
                            data={modelSelectionData}
                            onProductClick={handleProductClick}
                            loadSubranges={loadSubranges}
                            loadSearchResults={loadSearchResults}
                            loadFilters={loadFilters}
                            onFiltersChange={fetchWithFilters}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    {formatMessage({ id: 'common.done', defaultMessage: 'Done' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
